import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  COVERAGE_LABELS,
  COVERAGE_STATUSES,
  GENE_TESTED_SCOPES,
  METHOD_KINDS,
  METHOD_KIND_LABELS,
  SCOPE_LABELS,
  TRANSCRIPT_HELP,
  isVersionedTranscript,
  rowSymbolOf
} from './testRecordModel';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// One gene tested, edited (TGCLOUD-1866, Phase 2 rework): how much of it was studied, against
// which transcript, by which methods, and what the lab states about its coverage.
//
// THE LAB'S WORDS STAY THE LAB'S. Each coded value (a coverage status, a method's kind) sits beside
// the text as printed, and editing one never rewrites the other. A row applied from a lab report
// keeps its `labReportId` and `findingKeys` however it is edited: the save compares it with the
// reading and records each changed value as the clinician's (labReportRecord.js).
//
// Coverage has no "unknown" default beyond `not-stated`, which is a real answer: the lab said
// nothing. It never reads as "nothing reportable".

const blankToUndefined = (value) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;

// The row as edited, in the save's shape: blank printed text is left out, a blank transcript is
// null, and every field the row carried that this editor does not touch is kept.
function rowFromDraft(row, draft) {
  const next = { ...row, scope: draft.scope };
  const region = blankToUndefined(draft.regionDescriptionAsPrinted);
  if (draft.scope !== 'whole-gene' && region !== undefined) {
    next.regionDescriptionAsPrinted = region;
  } else {
    delete next.regionDescriptionAsPrinted;
  }
  next.transcript = draft.transcript.trim() || null;
  const coverage = { status: draft.coverageStatus };
  const printed = blankToUndefined(draft.coverageAsPrinted);
  if (printed !== undefined) coverage.asPrinted = printed;
  next.coverage = coverage;
  next.methods = draft.methods
    .filter((m) => m.kind)
    .map((m) => {
      const asPrinted = blankToUndefined(m.asPrinted);
      return asPrinted === undefined
        ? { kind: m.kind }
        : { kind: m.kind, asPrinted };
    });
  return next;
}

const draftOf = (row) => ({
  scope: row?.scope || 'whole-gene',
  regionDescriptionAsPrinted: row?.regionDescriptionAsPrinted || '',
  transcript: row?.transcript || '',
  coverageStatus: row?.coverage?.status || 'not-stated',
  coverageAsPrinted: row?.coverage?.asPrinted || '',
  methods: (row?.methods || []).map((m) => ({
    kind: m.kind,
    asPrinted: m.asPrinted || ''
  }))
});

/**
 * @param {object} props
 * @param {object|null} props.row  the genesTested row being edited; null when closed
 * @param {() => void} props.onClose
 * @param {(row: object) => void} props.onSave
 */
export default function GeneRowEditor({ row, onClose, onSave }) {
  const t = useAutoTranslation();

  const [draft, setDraft] = React.useState(() => draftOf(row));
  React.useEffect(() => {
    if (row) setDraft(draftOf(row));
  }, [row]);

  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const setMethod = (i, patch) =>
    setDraft((prev) => ({
      ...prev,
      methods: prev.methods.map((m, j) => (j === i ? { ...m, ...patch } : m))
    }));

  const transcript = draft.transcript.trim();
  const transcriptInvalid = !!transcript && !isVersionedTranscript(transcript);
  const symbol = row ? rowSymbolOf(row) : '';

  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="gene-row-editor-title"
    >
      <DialogTitle id="gene-row-editor-title" sx={{ pb: 1 }}>
        {t('Edit {{symbol}}', { symbol })}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              label={t('Scope')}
              value={draft.scope}
              onChange={(e) => set({ scope: e.target.value })}
            >
              {GENE_TESTED_SCOPES.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(SCOPE_LABELS[s])}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('Transcript')}
              value={draft.transcript}
              onChange={(e) => set({ transcript: e.target.value })}
              error={transcriptInvalid}
              helperText={
                transcriptInvalid
                  ? t('Enter a versioned transcript. {{transcriptHelp}}', {
                      transcriptHelp: t(TRANSCRIPT_HELP)
                    })
                  : t('Optional. {{transcriptHelp}}', {
                      transcriptHelp: t(TRANSCRIPT_HELP)
                    })
              }
            />
          </Grid>
          {draft.scope !== 'whole-gene' && (
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('Region studied, as printed')}
                value={draft.regionDescriptionAsPrinted}
                onChange={(e) =>
                  set({ regionDescriptionAsPrinted: e.target.value })
                }
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              label={t('Coverage')}
              value={draft.coverageStatus}
              onChange={(e) => set({ coverageStatus: e.target.value })}
              helperText={t(
                'What the laboratory states. Not stated never reads as nothing reportable.'
              )}
            >
              {COVERAGE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(COVERAGE_LABELS[s])}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              multiline
              label={t('Coverage, as printed')}
              value={draft.coverageAsPrinted}
              onChange={(e) => set({ coverageAsPrinted: e.target.value })}
            />
          </Grid>
          <Grid size={12}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>
              {t('Methods')}
            </Typography>
            {draft.methods.length === 0 && (
              <Typography
                variant="caption"
                component="div"
                sx={{ color: 'text.secondary', mb: 1 }}
              >
                {t('No method recorded.')}
              </Typography>
            )}
            {draft.methods.map((m, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  mb: 1
                }}
              >
                <TextField
                  select
                  size="small"
                  label={t('Method {{value}}', { value: i + 1 })}
                  value={m.kind}
                  onChange={(e) => setMethod(i, { kind: e.target.value })}
                  sx={{ minWidth: 190 }}
                >
                  {METHOD_KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {t(METHOD_KIND_LABELS[k])}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  label={t('Method {{value}}, as printed', { value: i + 1 })}
                  value={m.asPrinted}
                  onChange={(e) => setMethod(i, { asPrinted: e.target.value })}
                />
                <Tooltip title={t('Remove method')}>
                  <IconButton
                    aria-label={t('Remove method {{value}}', { value: i + 1 })}
                    onClick={() =>
                      setDraft((prev) => ({
                        ...prev,
                        methods: prev.methods.filter((_, j) => j !== i)
                      }))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setDraft((prev) => ({
                  ...prev,
                  methods: [
                    ...prev.methods,
                    { kind: 'sequencing', asPrinted: '' }
                  ]
                }))
              }
            >
              {t('Add a method')}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="secondary">
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={transcriptInvalid}
          onClick={() => onSave(rowFromDraft(row, draft))}
        >
          {t('Save gene')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
